import React, { useState, useEffect, useRef } from 'react';
import gallineroAsset from '../../../../assets/Gallinero.png';

// --- CONSTANTES DE RECURSOS POR DEFECTO ---
const ASSETS = {
    GALLO_NORMAL: '/img/games/pluckingTheRooster/Gallo1.png',
    GALLO_HIT: '/img/games/pluckingTheRooster/Gallo2.png',
    GALLO_BONUS_1: '/img/games/pluckingTheRooster/Gallo3.png',
    GALLO_BONUS_2: '/img/games/pluckingTheRooster/Gallo4.png',
    GALLO_BONUS_3: '/img/games/pluckingTheRooster/Gallo5.png',
    GALLINERO: gallineroAsset
};

const formatAssetUrl = (url, fallback = '') => {
    if (!url) return fallback;
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
    if (url.startsWith('/img/') || url.startsWith('/static/') || url.startsWith('static/')) return url;
    const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const baseUrl = API_URL.replace(/\/api\/?$/, '');
    return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
};

const INITIAL_GAME_DURATION = 15; // Segundos

const PluckingRooster = ({ onGameFinish, initialDuration = INITIAL_GAME_DURATION, customConfig = {} }) => {
    
    // --- EXTRACCIÓN DE ASSETS DINÁMICOS CON FALLBACK ---
    const characterNormal = customConfig.characterNormal ? formatAssetUrl(customConfig.characterNormal, ASSETS.GALLO_NORMAL) : ASSETS.GALLO_NORMAL;
    const characterHit = customConfig.characterHit ? formatAssetUrl(customConfig.characterHit, ASSETS.GALLO_HIT) : ASSETS.GALLO_HIT;
    const backObstacle = customConfig.backObstacle ? formatAssetUrl(customConfig.backObstacle, ASSETS.GALLINERO) : ASSETS.GALLINERO;
    const frontObstacle = customConfig.frontObstacle ? formatAssetUrl(customConfig.frontObstacle, '') : '';
    const bonusStage1 = customConfig.bonusStage1 ? formatAssetUrl(customConfig.bonusStage1, ASSETS.GALLO_BONUS_1) : ASSETS.GALLO_BONUS_1;
    const bonusStage2 = customConfig.bonusStage2 ? formatAssetUrl(customConfig.bonusStage2, ASSETS.GALLO_BONUS_2) : ASSETS.GALLO_BONUS_2;
    const bonusStage3 = customConfig.bonusStage3 ? formatAssetUrl(customConfig.bonusStage3, ASSETS.GALLO_BONUS_3) : ASSETS.GALLO_BONUS_3;

    // --- ESTADOS DEL JUEGO ---
    const [gameState, setGameState] = useState('countdown'); // countdown, playing, bonus, finished
    const [score, setScore] = useState(0);
    const [countdown, setCountdown] = useState(3);
    const [activeHoles, setActiveHoles] = useState(Array(6).fill(false)); // Qué hoyos tienen gallos arriba
    const [hitHoles, setHitHoles] = useState(Array(6).fill(false)); // Cuáles han sido golpeados (para cambiar img)
    const [bonusImage, setBonusImage] = useState(bonusStage1);
    
    // --- REFS PARA LOGICA DEL GAME LOOP ---
    const gameTimerRef = useRef(null);      // Timer principal del juego
    const peepTimeoutRef = useRef(null);    // Timer para esconder/sacar gallos
    const lastHoleRef = useRef(-1);         // Último hoyo usado para no repetir inmediato
    const scoreRef = useRef(0);             // Referencia mutable del score para callbacks síncronos
    const isGameActiveRef = useRef(false);  // Flag crítica para el loop de peep
    const containerRef = useRef(null);      // Para añadir efectos visuales al DOM

    const hasFinishedRef = useRef(false);

    // --- EFECTOS VISUALES TEMPORALES (FLASH) ---
    const [flashType, setFlashType] = useState(null); // 'hit', 'miss' o null

    // --- INICIO: COUNTDOWN ---
    useEffect(() => {
        if (gameState === 'countdown') {
            const timer = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        startGame();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [gameState]);

    // --- LÓGICA: INICIAR JUEGO ---
    const startGame = () => {
        setGameState('playing');
        setScore(0);
        scoreRef.current = 0;
        isGameActiveRef.current = true;
        
        // Iniciar el ciclo de topos (gallos)
        peep();

        // Iniciar Timer Principal
        let timeLeft = initialDuration;
        let elapsed = 0;
        
        gameTimerRef.current = setInterval(() => {
            timeLeft--;
            elapsed++;

            if (elapsed % 5 === 0 && timeLeft > 0) {
                spawnFloatingAlert("¡MÁS RÁPIDO!", "#ef4444");
            }

            if (timeLeft <= 0) {
                startBonusPhase();
            }
        }, 1000);
    };

    // --- LÓGICA: MOTOR DE APARICIÓN (PEEP) ---
    const peep = () => {
        if (!isGameActiveRef.current) return;

        // Tiempos aleatorios (entre 0.7s y 1.1s)
        const time = Math.round(Math.random() * (1100 - 700) + 700);
        
        // Elegir hoyo aleatorio distinto al anterior
        const holeIndex = getRandomHoleIndex();
        
        // Activar Gallo
        activateHole(holeIndex, true);

        peepTimeoutRef.current = setTimeout(() => {
            // Desactivar Gallo si sigue activo
            activateHole(holeIndex, false);
            
            // Si el juego sigue, llamar recursivamente
            if (isGameActiveRef.current) {
                peep();
            }
        }, time);
    };

    const getRandomHoleIndex = () => {
        let newIndex;
        do {
            newIndex = Math.floor(Math.random() * 6);
        } while (newIndex === lastHoleRef.current);
        
        lastHoleRef.current = newIndex;
        return newIndex;
    };

    const activateHole = (index, isActive) => {
        setActiveHoles(prev => {
            const copy = [...prev];
            copy[index] = isActive;
            return copy;
        });
        
        if (!isActive) {
            setHitHoles(prev => {
                const copy = [...prev];
                copy[index] = false;
                return copy;
            });
        }
    };

    // --- LÓGICA: TRANSICIÓN A BONUS (4 Segundos con evolución de etapas) ---
    const startBonusPhase = () => {
        clearInterval(gameTimerRef.current);
        clearTimeout(peepTimeoutRef.current);
        isGameActiveRef.current = false;
        
        // Limpiar tablero
        setActiveHoles(Array(6).fill(false));
        
        spawnFloatingAlert("¡BONUS!", "#fbbf24");

        setTimeout(() => {
            setGameState('bonus');
            setBonusImage(bonusStage1);

            // Transición por tiempo idéntica a ADSL:
            // 0s a 2s: bonusStage1
            // 2s a 3s: bonusStage2
            // 3s a 4s: bonusStage3
            setTimeout(() => setBonusImage(bonusStage2), 2000);
            setTimeout(() => setBonusImage(bonusStage3), 3000);

            // Terminar juego después de 4 segundos exactos de bonus
            setTimeout(() => {
                endGame();
            }, 4000);
        }, 1000);
    };

    // --- LÓGICA: FIN DEL JUEGO ---
    const endGame = () => {
        if (hasFinishedRef.current) return;
        
        hasFinishedRef.current = true; 
        
        clearInterval(gameTimerRef.current);
        clearTimeout(peepTimeoutRef.current);
        
        setGameState('finished');
        
        if (onGameFinish) {
            onGameFinish(scoreRef.current);
        }
    };

    // --- EVENT HANDLERS: INTERACCIÓN ---

    /**
     * Maneja el golpe exitoso a un personaje en la fase principal
     */
    const handleWhack = (e, index) => {
        e.stopPropagation();

        if (gameState !== 'playing') return;
        if (!activeHoles[index]) return;
        if (hitHoles[index]) return;

        updateScore(3);
        triggerFlash('hit');
        
        const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : window.innerWidth / 2);
        const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : window.innerHeight / 2);
        spawnHitText(clientX, clientY, "¡+3!");

        setHitHoles(prev => {
            const copy = [...prev];
            copy[index] = true;
            return copy;
        });

        setTimeout(() => activateHole(index, false), 500);
    };

    /**
     * Maneja el click fallido (tap en el fondo o hoyo vacío)
     */
    const handleMiss = (e) => {
        if (gameState === 'playing') {
            updateScore(-1);
            triggerFlash('miss');
        } else if (gameState === 'bonus') {
            triggerFlash('miss');
        }
    };

    /**
     * Maneja los taps rápidos durante la fase de Bonus (Suma exactamente +1 por tap)
     */
    const handleBonusTap = (e) => {
        e.stopPropagation();
        if (gameState !== 'bonus') return;

        updateScore(1);
        triggerFlash('hit');

        const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : window.innerWidth / 2);
        const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : window.innerHeight / 2);
        spawnHitText(clientX, clientY, "¡+1!");
    };

    // --- HELPERS DE UTILIDAD ---

    const updateScore = (points) => {
        setScore(prev => {
            const newScore = Math.max(0, prev + points);
            scoreRef.current = newScore;
            return newScore;
        });
    };

    const triggerFlash = (type) => {
        setFlashType(null);
        setTimeout(() => setFlashType(type), 10);
        setTimeout(() => setFlashType(null), 210);
    };

    const spawnHitText = (x, y, text) => {
        if (!containerRef.current) return;
        
        const el = document.createElement('div');
        el.className = 'rooster-game__hit-effect';
        el.style.left = `${x}px`;
        el.style.top = `${y}px`;
        el.innerText = text;
        
        containerRef.current.appendChild(el);
        setTimeout(() => el.remove(), 400);
    };

    const spawnFloatingAlert = (text, color) => {
        if (!containerRef.current) return;

        const el = document.createElement('div');
        el.className = 'rooster-game__status-alert';
        el.style.color = color;
        el.innerText = text;

        containerRef.current.appendChild(el);
        setTimeout(() => el.remove(), 1000);
    };

    // --- RENDER ---
    return (
        <div className="rooster-game" ref={containerRef}>
            
            {/* FLASH OVERLAYS */}
            <div className={`rooster-game__flash rooster-game__flash--hit ${flashType === 'hit' ? 'rooster-game__flash--active' : ''}`} />
            <div className={`rooster-game__flash rooster-game__flash--miss ${flashType === 'miss' ? 'rooster-game__flash--active' : ''}`} />

            {/* COUNTDOWN OVERLAY */}
            {gameState === 'countdown' && (
                <div className="rooster-game__overlay rooster-game__overlay--countdown">
                    <div className="rooster-game__countdown-text">{countdown}</div>
                </div>
            )}

            {/* BONUS STAGE OVERLAY */}
            {gameState === 'bonus' && (
                <div className="rooster-game__overlay rooster-game__overlay--bonus" onClick={handleMiss}>
                    <div className="rooster-game__tap-container">
                        {[...Array(10)].map((_, i) => (
                            <div 
                                key={i} 
                                className="rooster-game__tap-label"
                                style={{
                                    left: `${Math.random() * 70 + 15}%`,
                                    top: `${Math.random() * 70 + 15}%`,
                                    animationDelay: `${Math.random() * 0.5}s`
                                }}
                            >
                                TAP
                            </div>
                        ))}
                    </div>
                    <div 
                        className="rooster-game__bonus-gallo" 
                        onPointerDown={handleBonusTap}
                    >
                        <img src={bonusImage} alt="Bonus Character" />
                    </div>
                </div>
            )}

            {/* GAME GRID (MAIN STAGE WITH 3-LAYER SANDWICH STRUCTURE) */}
            <div className="rooster-game__main-wrapper" onPointerDown={handleMiss}>
                <div className="rooster-game__grid">
                    {activeHoles.map((isActive, index) => (
                        <div key={index} className="rooster-game__hole">
                            {/* Capa 1 (Profundidad Trasera) z-index: 1 */}
                            <div className="rooster-game__hole-back">
                                {backObstacle && <img src={backObstacle} alt="Hole Back" />}
                            </div>

                            {/* Capa 2 (Personaje Animado) z-index: 2 */}
                            <div 
                                className={`rooster-game__rooster ${isActive ? 'rooster-game__rooster--active' : ''}`}
                                onPointerDown={(e) => handleWhack(e, index)}
                                data-hit={hitHoles[index]}
                            >
                                <img 
                                    src={hitHoles[index] ? characterHit : characterNormal} 
                                    alt="Personaje" 
                                />
                            </div>

                            {/* Capa 3 (Obstáculo Frontal) z-index: 3 */}
                            <div className="rooster-game__hole-front" style={{ pointerEvents: 'none' }}>
                                {frontObstacle ? <img src={frontObstacle} alt="Hole Front" /> : null}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* RESULT MODAL */}
            {gameState === 'finished' && (
                <div className="rooster-game__overlay rooster-game__overlay--result">
                    <div className="rooster-game__result-card">
                        <h2>¡TIEMPO!</h2>
                        <p>Puntuación Total</p>
                        <div className="rooster-game__score-final">{score}</div>
                        <p>Puntos</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export { PluckingRooster };